import {
  applyServerChanges,
  buildMutations,
  flattenState,
  recordIndexKey,
  type CloudMutation,
  type HashIndex,
  type LegacyState,
  type ServerChange,
} from "./state-codec";

export type SyncRequest = Readonly<{
  cursor: number;
  schemaVersion: 7;
  clientBuild: string;
  changes: CloudMutation[];
}>;

export type SyncResponse = Readonly<{
  cursor: number;
  acceptedMutationIds: string[];
  changes: ServerChange[];
  conflicts: ReadonlyArray<Readonly<Record<string, unknown>>>;
  hasMore: boolean;
  serverTime: number;
  minimumClientBuild: string;
}>;

export type CachedWorkspace = Readonly<{
  uid: string;
  schemaVersion: 7;
  cursor: number;
  state: LegacyState;
  hashIndex: HashIndex;
  updatedAt: number;
  claimCompletedAt?: number;
}>;

export interface CloudApi {
  sync(request: SyncRequest): Promise<SyncResponse>;
}

export interface CloudCacheStore {
  loadWorkspace(uid: string): Promise<CachedWorkspace | null>;
  saveWorkspace(workspace: CachedWorkspace): Promise<void>;
  saveStateAndMutations(
    workspace: CachedWorkspace,
    mutations: readonly CloudMutation[],
  ): Promise<void>;
  listOutbox(uid: string): Promise<CloudMutation[]>;
  deleteOutbox(uid: string, mutationIds: readonly string[]): Promise<void>;
  clearUid(uid: string): Promise<void>;
}

const CLIENT_BUILD = "2.0.0";
const MAX_BATCH_MUTATIONS = 200;
const MAX_BATCH_BYTES = 1024 * 1024;

function emptyWorkspace(uid: string): CachedWorkspace {
  return {
    uid,
    schemaVersion: 7,
    cursor: 0,
    state: { version: 7, deleted: {} },
    hashIndex: {},
    updatedAt: Date.now(),
  };
}

function pendingKey(mutation: CloudMutation): string {
  return recordIndexKey(mutation.collection, mutation.recordId);
}

function coalesceMutations(
  pending: readonly CloudMutation[],
  proposed: readonly CloudMutation[],
): CloudMutation[] {
  const byRecord = new Map(pending.map((mutation) => [pendingKey(mutation), mutation]));
  for (const mutation of proposed) {
    const key = pendingKey(mutation);
    const existing = byRecord.get(key);
    byRecord.set(
      key,
      existing === undefined
        ? mutation
        : {
            ...mutation,
            mutationId: existing.mutationId,
            baseRecordRevision: existing.baseRecordRevision,
          },
    );
  }
  return [...byRecord.values()];
}

function boundedBatch(mutations: readonly CloudMutation[]): CloudMutation[] {
  const batch: CloudMutation[] = [];
  let bytes = 0;
  for (const mutation of mutations) {
    const mutationBytes = new TextEncoder().encode(JSON.stringify(mutation)).byteLength + 1;
    if (batch.length > 0 && bytes + mutationBytes > MAX_BATCH_BYTES) break;
    if (mutationBytes > MAX_BATCH_BYTES) {
      throw new RangeError("A queued cloud mutation exceeds the sync request limit");
    }
    batch.push(mutation);
    bytes += mutationBytes;
    if (batch.length >= MAX_BATCH_MUTATIONS) break;
  }
  return batch;
}

function acknowledgedIndex(
  current: Readonly<HashIndex>,
  changes: readonly ServerChange[],
): HashIndex {
  const next: HashIndex = { ...current };
  for (const change of changes) {
    next[recordIndexKey(change.collection, change.recordId)] = {
      contentHash: change.contentHash,
      serverRevision: change.serverRevision,
    };
  }
  return next;
}

export class CloudRepository {
  private workspace: CachedWorkspace | null = null;

  constructor(
    private readonly uid: string,
    private readonly api: CloudApi,
    private readonly cache: CloudCacheStore,
    private readonly createMutationId: () => string = () => crypto.randomUUID(),
  ) {
    if (uid.length === 0) throw new TypeError("A Firebase UID is required");
  }

  private async currentWorkspace(): Promise<CachedWorkspace> {
    if (this.workspace !== null) return this.workspace;
    this.workspace = (await this.cache.loadWorkspace(this.uid)) ?? emptyWorkspace(this.uid);
    return this.workspace;
  }

  async queueState(state: LegacyState): Promise<void> {
    const current = await this.currentWorkspace();
    const [records, pending] = await Promise.all([
      flattenState(state),
      this.cache.listOutbox(this.uid),
    ]);
    const proposed = buildMutations(
      records,
      state.deleted ?? {},
      current.hashIndex,
      this.createMutationId,
    );
    const outbox = coalesceMutations(pending, proposed);
    this.workspace = {
      ...current,
      state: structuredClone(state),
      updatedAt: Date.now(),
    };
    await this.cache.saveStateAndMutations(this.workspace, outbox);
  }

  async reconcile(baseState?: LegacyState): Promise<LegacyState> {
    const current = await this.currentWorkspace();
    if (baseState !== undefined && Object.keys(current.state).length <= 2) {
      this.workspace = { ...current, state: structuredClone(baseState) };
    }
    return this.syncNow();
  }

  async syncNow(): Promise<LegacyState> {
    let current = await this.currentWorkspace();
    let requestCount = 0;
    let firstRequest = true;

    while (requestCount < 100) {
      const pending = await this.cache.listOutbox(this.uid);
      const batch = boundedBatch(pending);
      if (!firstRequest && batch.length === 0) break;
      firstRequest = false;
      requestCount += 1;

      const response = await this.api.sync({
        cursor: current.cursor,
        schemaVersion: 7,
        clientBuild: CLIENT_BUILD,
        changes: batch,
      });
      if (response.changes.some((change) => change.ownerUid !== this.uid)) {
        throw new Error("Cloud workspace mismatch");
      }
      const state = applyServerChanges(current.state, response.changes);
      current = {
        ...current,
        cursor: response.cursor,
        state,
        hashIndex: acknowledgedIndex(current.hashIndex, response.changes),
        updatedAt: response.serverTime,
      };
      this.workspace = current;
      await this.cache.deleteOutbox(this.uid, response.acceptedMutationIds);
      await this.cache.saveWorkspace(current);

      const remaining = await this.cache.listOutbox(this.uid);
      if (!response.hasMore && remaining.length === 0) break;
    }
    if (requestCount >= 100) throw new Error("Cloud synchronization did not converge");
    return structuredClone(current.state);
  }

  async pendingCount(): Promise<number> {
    return (await this.cache.listOutbox(this.uid)).length;
  }

  async hasCloudRecords(): Promise<boolean> {
    const current = await this.currentWorkspace();
    return Object.keys(current.hashIndex).length > 0;
  }

  async cachedState(): Promise<LegacyState> {
    return structuredClone((await this.currentWorkspace()).state);
  }

  async hasCompletedClaim(): Promise<boolean> {
    return (await this.currentWorkspace()).claimCompletedAt !== undefined;
  }

  async markClaimCompleted(): Promise<void> {
    const current = await this.currentWorkspace();
    this.workspace = { ...current, claimCompletedAt: Date.now() };
    await this.cache.saveWorkspace(this.workspace);
  }

  async clearForLogout(): Promise<void> {
    if ((await this.pendingCount()) > 0) await this.syncNow();
    if ((await this.pendingCount()) > 0) {
      throw new Error("Cloud changes are still pending");
    }
    await this.cache.clearUid(this.uid);
    this.workspace = null;
  }
}
